<template>
  <div class="p-2 sm:p-4 text-lexend overflow-x-hidden min-h-screen bg-gray-50 dark:bg-gray-900/50">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-3 sm:mb-5 mt-1">
      <div class="animate-fadeIn w-full sm:w-auto">
        <div class="flex items-center gap-2 mb-1">
          <div class="w-8 h-1 bg-emerald-600 rounded-full"></div>
          <span class="text-[10px] sm:text-xs font-black text-emerald-600 tracking-widest">Panel de Control</span>
        </div>
        <h1 class="text-xl sm:text-3xl font-black text-gray-900 dark:text-white tracking-tight flex items-center flex-wrap gap-2">
          Préstamos Personales
          <span class="px-2 sm:px-3 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 text-xs sm:text-sm font-black rounded-lg border border-emerald-200 dark:border-emerald-800">
            {{ prestamos.length }}
          </span>
        </h1>
      </div>
      <div class="flex items-center gap-2 w-full sm:w-auto flex-wrap justify-end">
        <router-link to="/prestamos/nuevo" class="px-3 sm:px-5 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-[10px] sm:text-xs tracking-widest transition-all shadow-md flex items-center gap-1 sm:gap-2">
          <Icon icon="mdi:plus-circle" class="text-base sm:text-lg" />
          <span class="hidden sm:inline">Nuevo Préstamo</span>
          <span class="sm:hidden">Nuevo</span>
        </router-link>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-5 mb-3 sm:mb-5 animate-fadeInUp">
      <div class="grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-5 items-end">
        <div class="sm:col-span-4 lg:col-span-5">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Buscar</label>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Número, prestatario, documento..."
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl outline-none dark:text-white font-bold text-sm"
            @input="handleSearch"
          />
        </div>
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Estado</label>
          <select
            v-model="filterEstado"
            @change="loadPrestamos"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-gray-800 dark:text-white outline-none appearance-none cursor-pointer text-sm"
          >
            <option value="all">TODOS</option>
            <option value="activo">ACTIVOS</option>
            <option value="pagado">PAGADOS</option>
            <option value="vencido">VENCIDOS</option>
            <option value="parcial">PARCIALES</option>
            <option value="cancelado">CANCELADOS</option>
          </select>
        </div>
        <div class="sm:col-span-3 lg:col-span-2">
          <label class="block text-[10px] sm:text-[11px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2 ml-1">Tipo</label>
          <select
            v-model="filterTipo"
            @change="loadPrestamos"
            class="w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-100 dark:border-gray-700 focus:border-emerald-500 rounded-xl font-black text-gray-800 dark:text-white outline-none appearance-none cursor-pointer text-sm"
          >
            <option value="all">TODOS</option>
            <option value="empleado">EMPLEADOS</option>
            <option value="cliente">CLIENTES</option>
            <option value="externo">EXTERNOS</option>
          </select>
        </div>
        <div class="sm:col-span-2 lg:col-span-3 flex gap-2">
          <button @click="loadPrestamos" class="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-xl font-black text-[10px] sm:text-xs hover:bg-gray-200 transition-all flex items-center justify-center gap-1">
            <Icon icon="mdi:refresh" :class="{'animate-spin': loading}" />
            <span class="hidden sm:inline">Actualizar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Estadísticas Rápidas -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-5 animate-fadeInUp delay-100">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Total</p>
            <p class="text-xl sm:text-2xl font-black text-gray-900 dark:text-white">{{ prestamos.length }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-gray-100 dark:bg-gray-700 rounded-xl">
            <Icon icon="mdi:hand-coin" class="text-gray-600 dark:text-gray-400 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Activos</p>
            <p class="text-xl sm:text-2xl font-black text-blue-600">{{ estadisticas.prestamosActivos || 0 }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
            <Icon icon="mdi:check-circle" class="text-blue-600 dark:text-blue-400 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Vencidos</p>
            <p class="text-xl sm:text-2xl font-black text-rose-600">{{ estadisticas.prestamosVencidos || 0 }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-rose-50 dark:bg-rose-900/30 rounded-xl">
            <Icon icon="mdi:alert-circle" class="text-rose-600 dark:text-rose-400 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-4">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest uppercase">Deuda Total</p>
            <p class="text-xl sm:text-2xl font-black text-emerald-600">{{ formatCurrency(estadisticas.totalDeudaUsd || 0) }}</p>
          </div>
          <div class="p-2 sm:p-3 bg-emerald-50 dark:bg-emerald-900/30 rounded-xl">
            <Icon icon="mdi:currency-usd" class="text-emerald-600 dark:text-emerald-400 text-xl sm:text-2xl" />
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla / Tarjetas -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden animate-fadeInUp delay-200">
      <!-- Versión Desktop: Tabla -->
      <div class="hidden lg:block overflow-x-auto custom-scrollbar">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">N° Préstamo</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Prestatario</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Tipo</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Monto</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Saldo</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest">Vencimiento</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-center">Estado</th>
              <th class="px-5 py-4 text-[11px] font-black text-gray-400 tracking-widest text-right">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="loading" v-for="i in 5" :key="'load-'+i" class="animate-pulse">
              <td colspan="8" class="px-5 py-4"><div class="h-4 bg-gray-100 dark:bg-gray-700 rounded w-full"></div></td>
            </tr>
            <tr v-if="!loading && filteredPrestamos.length === 0">
              <td colspan="8" class="px-5 py-16 text-center">
                <div class="flex flex-col items-center opacity-30 select-none">
                  <Icon icon="mdi:hand-coin" class="text-7xl mb-3" />
                  <h4 class="text-xl font-black text-gray-400 tracking-widest">No hay préstamos</h4>
                  <p class="text-sm text-gray-300 mt-1">Crea tu primer préstamo</p>
                  <router-link to="/prestamos/nuevo" class="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-xl font-black text-xs hover:bg-emerald-700 transition-all">Nuevo Préstamo</router-link>
                </div>
              </td>
            </tr>
            <tr v-for="prestamo in paginatedPrestamos" :key="prestamo.idPrestamo" class="hover:bg-emerald-50/30 dark:hover:bg-gray-700/30 transition-all group">
              <td class="px-5 py-4">
                <div class="flex items-center gap-2">
                  <div class="h-8 w-8 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center">
                    <Icon icon="mdi:receipt-text" class="text-emerald-600 dark:text-emerald-400 text-sm" />
                  </div>
                  <span class="text-sm font-black text-gray-900 dark:text-white">{{ prestamo.numeroPrestamo }}</span>
                </div>
              </td>
              <td class="px-5 py-4">
                <div>
                  <div class="text-sm font-bold text-gray-900 dark:text-white">{{ prestamo.nombrePrestatario || prestamo.nombreUsuario || prestamo.nombreCliente || 'N/A' }}</div>
                  <div class="text-[10px] text-gray-500 dark:text-gray-400">{{ prestamo.cedulaPrestatario || '' }}</div>
                </div>
              </td>
              <td class="px-5 py-4">
                <span class="px-2 py-1 rounded-lg text-[10px] font-black" :class="getTipoClass(prestamo.tipoPrestatario)">
                  <Icon :icon="getTipoIcon(prestamo.tipoPrestatario)" class="inline mr-1 text-xs" />
                  {{ formatearTipo(prestamo.tipoPrestatario) }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="font-black text-gray-900 dark:text-white">{{ formatCurrency(prestamo.montoUsd) }}</div>
                <div class="text-[10px] text-gray-500">{{ formatCurrencyBs(prestamo.montoBs) }}</div>
              </td>
              <td class="px-5 py-4">
                <div class="font-black text-emerald-600">{{ formatCurrency(prestamo.saldoPendienteUsd) }}</div>
                <div class="text-[10px] text-gray-500">{{ formatCurrencyBs(prestamo.saldoPendienteBs) }}</div>
              </td>
              <td class="px-5 py-4">
                <div class="text-sm font-bold text-gray-900 dark:text-white">{{ formatDate(prestamo.fechaVencimiento) }}</div>
                <div class="text-[10px]" :class="getVencimientoClass(prestamo.fechaVencimiento)">
                  {{ getDiasTexto(prestamo.fechaVencimiento) }}
                </div>
              </td>
              <td class="px-5 py-4 text-center">
                <span class="px-3 py-1 inline-flex text-[10px] font-black uppercase tracking-widest rounded-lg border" :class="getEstadoClass(prestamo.estado)">
                  <Icon :icon="getEstadoIcon(prestamo.estado)" class="mr-1.5 text-xs" />
                  {{ formatearEstado(prestamo.estado) }}
                </span>
              </td>
              <td class="px-5 py-4 text-right">
                <div class="flex items-center justify-end gap-1 flex-nowrap">
                  <router-link :to="`/prestamos/${prestamo.idPrestamo}`" title="Ver detalles" class="p-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm">
                    <Icon icon="mdi:eye" class="text-base" />
                  </router-link>
                  <button v-if="prestamo.estado !== 'pagado' && prestamo.estado !== 'cancelado'" @click="abrirModalPago(prestamo)" title="Registrar pago" class="p-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
                    <Icon icon="mdi:cash" class="text-base" />
                  </button>
                  <button v-if="prestamo.estado !== 'pagado' && prestamo.estado !== 'cancelado'" @click="abrirModalCancelar(prestamo)" title="Cancelar" class="p-1.5 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm">
                    <Icon icon="mdi:close-circle" class="text-base" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Versión Mobile: Tarjetas -->
      <div class="lg:hidden space-y-3 p-3">
        <div v-if="loading" v-for="i in 3" :key="'load-mobile-'+i" class="bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 animate-pulse">
          <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
        
        <div v-if="!loading && filteredPrestamos.length === 0" class="bg-gray-50 dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
          <div class="flex flex-col items-center opacity-30 select-none">
            <Icon icon="mdi:hand-coin" class="text-6xl mb-3" />
            <h4 class="text-lg font-black text-gray-400 tracking-widest">No hay préstamos</h4>
            <p class="text-sm text-gray-300 mt-1">Crea tu primer préstamo</p>
            <router-link to="/prestamos/nuevo" class="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-xl font-black text-xs hover:bg-emerald-700 transition-all">Nuevo Préstamo</router-link>
          </div>
        </div>
        
        <div v-for="prestamo in paginatedPrestamos" :key="prestamo.idPrestamo" 
             class="bg-white dark:bg-gray-800 rounded-2xl shadow-md border border-gray-200 dark:border-gray-700 p-4 hover:shadow-lg transition-all duration-200">
          
          <div class="flex flex-wrap items-center justify-between gap-2 mb-3 pb-2 border-b border-gray-100 dark:border-gray-700">
            <div class="flex items-center gap-2">
              <div class="h-8 w-8 bg-emerald-100 dark:bg-emerald-900/40 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon icon="mdi:receipt-text" class="text-emerald-600 dark:text-emerald-400 text-sm" />
              </div>
              <span class="text-sm font-black text-gray-900 dark:text-white">{{ prestamo.numeroPrestamo }}</span>
            </div>
            <span class="px-2 py-0.5 inline-flex text-[8px] font-black uppercase tracking-widest rounded-lg border" :class="getEstadoClass(prestamo.estado)">
              <Icon :icon="getEstadoIcon(prestamo.estado)" class="mr-1 text-xs" />
              {{ formatearEstado(prestamo.estado) }}
            </span>
          </div>

          <div class="mb-2">
            <p class="font-black text-gray-900 dark:text-white text-sm">{{ prestamo.nombrePrestatario || prestamo.nombreUsuario || prestamo.nombreCliente || 'N/A' }}</p>
            <p class="text-[10px] text-gray-500 dark:text-gray-400">{{ prestamo.cedulaPrestatario || '' }}</p>
          </div>

          <div class="grid grid-cols-2 gap-2 mb-2 text-xs">
            <div>
              <span class="text-[8px] font-black text-gray-400 uppercase tracking-widest">Monto</span>
              <p class="font-black text-gray-900 dark:text-white">{{ formatCurrency(prestamo.montoUsd) }}</p>
              <p class="text-[9px] text-gray-500">{{ formatCurrencyBs(prestamo.montoBs) }}</p>
            </div>
            <div>
              <span class="text-[8px] font-black text-gray-400 uppercase tracking-widest">Saldo</span>
              <p class="font-black text-emerald-600">{{ formatCurrency(prestamo.saldoPendienteUsd) }}</p>
              <p class="text-[9px] text-gray-500">{{ formatCurrencyBs(prestamo.saldoPendienteBs) }}</p>
            </div>
          </div>

          <div class="flex items-center justify-between mb-3 bg-gray-50 dark:bg-gray-900/30 rounded-xl p-2">
            <div>
              <span class="text-[8px] font-black text-gray-400 uppercase tracking-widest">Vencimiento</span>
              <p class="font-black text-gray-900 dark:text-white text-xs">{{ formatDate(prestamo.fechaVencimiento) }}</p>
            </div>
            <div class="text-right">
              <span class="text-[8px] font-black text-gray-400 uppercase tracking-widest">Tipo</span>
              <p>
                <span class="px-2 py-0.5 rounded-lg text-[8px] font-black" :class="getTipoClass(prestamo.tipoPrestatario)">
                  <Icon :icon="getTipoIcon(prestamo.tipoPrestatario)" class="inline mr-0.5 text-[10px]" />
                  {{ formatearTipo(prestamo.tipoPrestatario) }}
                </span>
              </p>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-1.5 pt-2 border-t border-gray-200 dark:border-gray-700">
            <router-link :to="`/prestamos/${prestamo.idPrestamo}`" title="Ver detalles" class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-sm">
              <Icon icon="mdi:eye" class="text-sm" />
            </router-link>
            <button v-if="prestamo.estado !== 'pagado' && prestamo.estado !== 'cancelado'" @click="abrirModalPago(prestamo)" title="Registrar pago" class="p-2 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-lg hover:bg-emerald-600 hover:text-white transition-all shadow-sm">
              <Icon icon="mdi:cash" class="text-sm" />
            </button>
            <button v-if="prestamo.estado !== 'pagado' && prestamo.estado !== 'cancelado'" @click="abrirModalCancelar(prestamo)" title="Cancelar" class="p-2 bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm">
              <Icon icon="mdi:close-circle" class="text-sm" />
            </button>
            <span v-if="prestamo.estado === 'pagado' || prestamo.estado === 'cancelado'" class="text-[8px] font-black text-gray-400 uppercase tracking-widest ml-auto">
              {{ prestamo.estado === 'pagado' ? '✓ Completado' : '✕ Cancelado' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Paginación -->
      <div v-if="filteredPrestamos.length > 0" class="bg-gray-50/50 dark:bg-gray-800/50 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div class="text-[10px] sm:text-[11px] font-black text-gray-400 uppercase tracking-widest text-center sm:text-left">
          MOSTRANDO {{ startItem }} - {{ endItem }} DE {{ filteredPrestamos.length }}
        </div>
        <div class="flex items-center gap-1 flex-wrap justify-center">
          <button @click="currentPage = Math.max(currentPage - 1, 1)" :disabled="currentPage === 1" class="p-1.5 rounded-lg border transition-all" :class="currentPage === 1 ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm'">
            <Icon icon="mdi:chevron-left" class="text-base sm:text-2xl" />
          </button>
          <div class="hidden sm:flex items-center gap-0.5 sm:gap-1 mx-1 sm:mx-2">
            <button v-for="page in visiblePages" :key="page" @click="currentPage = page" :class="['w-7 h-7 sm:w-9 sm:h-9 rounded-lg text-[10px] sm:text-xs font-black transition-all flex items-center justify-center', currentPage === page ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/30' : 'text-gray-500 hover:bg-white dark:hover:bg-gray-700 border border-transparent hover:border-gray-200']">
              {{ page }}
            </button>
          </div>
          <span class="sm:hidden text-xs font-black text-gray-600 dark:text-gray-300 px-2">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage = Math.min(currentPage + 1, totalPages)" :disabled="currentPage === totalPages" class="p-1.5 rounded-lg border transition-all" :class="currentPage === totalPages ? 'opacity-30 cursor-not-allowed border-gray-200' : 'hover:bg-white dark:hover:bg-gray-700 border-gray-200 dark:border-gray-600 shadow-sm'">
            <Icon icon="mdi:chevron-right" class="text-base sm:text-2xl" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Pago - Con Tasa Automática -->
    <Teleport to="body">
      <div v-if="showPagoModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9998]" @click.self="showPagoModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[95%] sm:max-w-md max-h-[95vh] overflow-y-auto animate-zoomIn mx-2 sm:mx-0">
          <div class="bg-gradient-to-r from-emerald-600 to-teal-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 sticky top-0 z-10 rounded-t-2xl">
            <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl flex-shrink-0">
              <Icon icon="mdi:cash" class="text-white text-lg sm:text-xl" />
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-xs sm:text-sm font-black text-white truncate">Registrar Pago</h3>
              <p class="text-[10px] text-white/70 font-bold truncate">{{ prestamoSeleccionado?.numeroPrestamo || '' }}</p>
            </div>
            <button @click="showPagoModal = false" class="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-all flex-shrink-0">
              <Icon icon="mdi:close" class="text-white text-lg sm:text-xl" />
            </button>
          </div>
          <div class="p-4 sm:p-6">
            <form @submit.prevent="registrarPago">
              <div class="space-y-3 sm:space-y-4">
                <div class="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-3">
                  <div class="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div>
                      <p class="text-[8px] sm:text-[10px] font-black text-gray-400">Monto Total</p>
                      <p class="font-black text-gray-900 dark:text-white text-sm sm:text-base">{{ formatCurrency(prestamoSeleccionado?.totalDeudaUsd || 0) }}</p>
                    </div>
                    <div>
                      <p class="text-[8px] sm:text-[10px] font-black text-gray-400">Saldo Pendiente</p>
                      <p class="font-black text-emerald-600 text-sm sm:text-base">{{ formatCurrency(prestamoSeleccionado?.saldoPendienteUsd || 0) }}</p>
                    </div>
                  </div>
                </div>

                <!-- Tasa de Cambio Automática -->
                <div>
                  <label class="block text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2">
                    Tasa de Cambio (Bs/USD)
                    <span v-if="cargandoTasa" class="text-[8px] text-emerald-500 ml-1">
                      <Icon icon="mdi:loading" class="animate-spin inline" /> Cargando...
                    </span>
                    <span v-else class="text-[8px] text-gray-400 ml-1">(Automática)</span>
                  </label>
                  <div class="relative">
                    <input 
                      v-model.number="pagoForm.tasaBsUsd" 
                      type="number" 
                      step="0.01" 
                      min="0" 
                      required 
                      class="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all cursor-default"
                      readonly
                      :class="{'opacity-70': cargandoTasa}"
                    />
                    <button 
                      @click="cargarTasaVenta" 
                      type="button"
                      class="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-emerald-600 transition-colors"
                      title="Actualizar tasa"
                    >
                      <Icon icon="mdi:refresh" :class="{'animate-spin': cargandoTasa}" class="text-base" />
                    </button>
                  </div>
                  <p class="text-[8px] text-gray-400 mt-1">
                    <Icon icon="mdi:information" class="inline text-[10px]" />
                    Tasa cargada automáticamente del sistema
                  </p>
                </div>

                <div>
                  <label class="block text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2">Monto a Pagar (USD)</label>
                  <input 
                    v-model.number="pagoForm.montoUsd" 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    class="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all" 
                    placeholder="0.00"
                    @input="calcularMontoBs"
                  />
                </div>

                <div>
                  <label class="block text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2">Monto a Pagar (Bs) <span class="text-[8px] font-normal text-gray-400">(Calculado)</span></label>
                  <input 
                    v-model.number="pagoForm.montoBs" 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    class="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 dark:bg-gray-700/50 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none transition-all cursor-default"
                    readonly
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label class="block text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2">Método de Pago</label>
                  <select v-model="pagoForm.metodoPago" class="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-bold text-gray-800 dark:text-white outline-none appearance-none cursor-pointer">
                    <option value="efectivo">Efectivo</option>
                    <option value="transferencia">Transferencia</option>
                    <option value="pago_movil">Pago Móvil</option>
                    <option value="zelle">Zelle</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>

                <div>
                  <label class="block text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2">Referencia</label>
                  <input v-model="pagoForm.referencia" type="text" class="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none transition-all" placeholder="Número de referencia" />
                </div>

                <div>
                  <label class="block text-[8px] sm:text-[10px] font-black text-gray-400 tracking-widest mb-1 sm:mb-2">Observaciones</label>
                  <textarea v-model="pagoForm.observaciones" rows="2" class="w-full px-3 sm:px-4 py-2 sm:py-2.5 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 focus:border-emerald-500 rounded-xl text-sm font-medium text-gray-800 dark:text-white outline-none transition-all resize-none" placeholder="Observaciones..."></textarea>
                </div>
              </div>

              <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4 mt-3 sm:mt-4 border-t border-gray-200 dark:border-gray-700">
                <button type="button" @click="showPagoModal = false" class="w-full sm:flex-1 py-2.5 sm:py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-50 transition-all">Cancelar</button>
                <button type="submit" :disabled="registrandoPago" class="w-full sm:flex-[2] py-2.5 sm:py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-emerald-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
                  <Icon :icon="registrandoPago ? 'mdi:loading' : 'mdi:cash'" :class="{'animate-spin': registrandoPago}" />
                  {{ registrandoPago ? 'Procesando...' : 'Registrar Pago' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal de Confirmación -->
    <Teleport to="body">
      <div v-if="showConfirmModal" class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-[9999]" @click.self="showConfirmModal = false">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-[95%] sm:max-w-md max-h-[95vh] overflow-y-auto animate-zoomIn mx-2 sm:mx-0">
          <div class="bg-gradient-to-r from-rose-600 to-pink-600 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-3 rounded-t-2xl">
            <div class="p-1.5 sm:p-2 bg-white/20 rounded-xl flex-shrink-0">
              <Icon icon="mdi:alert-circle" class="text-white text-lg sm:text-xl" />
            </div>
            <div>
              <h3 class="text-xs sm:text-sm font-black text-white">Cancelar Préstamo</h3>
              <p class="text-[9px] sm:text-[10px] text-white/70 font-bold">Esta acción no se puede deshacer</p>
            </div>
          </div>
          <div class="p-4 sm:p-6">
            <p class="text-sm text-gray-700 dark:text-gray-300">¿Estás seguro de que deseas cancelar el préstamo <span class="font-black">{{ prestamoSeleccionado?.numeroPrestamo }}</span>?</p>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">El préstamo quedará marcado como cancelado y no se podrán registrar más pagos.</p>
          </div>
          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button @click="showConfirmModal = false" class="w-full sm:flex-1 py-2.5 sm:py-3 border-2 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 rounded-xl font-black text-[10px] sm:text-xs tracking-widest hover:bg-gray-100 transition-all">Cancelar</button>
            <button @click="confirmarCancelacion" :disabled="cancelando" class="w-full sm:flex-[2] py-2.5 sm:py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-widest hover:opacity-90 transition-all shadow-lg shadow-rose-500/30 disabled:opacity-50 flex items-center justify-center gap-2">
              <Icon :icon="cancelando ? 'mdi:loading' : 'mdi:check'" :class="{'animate-spin': cancelando}" />
              {{ cancelando ? 'Procesando...' : 'Confirmar Cancelación' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { debounce } from 'lodash';
import {
  obtenerPrestamos,
  obtenerPrestamosActivos,
  obtenerPrestamosVencidos,
  obtenerPrestamosPorEstado,
  obtenerPrestamosPorTipo,
  obtenerEstadisticasPrestamos,
  cancelarPrestamo,
  registrarPagoRapido
} from '@/api/prestamos.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { getCurrentUser } from '@/utils/permissionUtils';

export default {
  name: 'PrestamosList',
  components: { Icon },
  data() {
    return {
      prestamos: [],
      loading: false,
      searchQuery: '',
      filterEstado: 'all',
      filterTipo: 'all',
      currentPage: 1,
      itemsPerPage: 10,
      estadisticas: {},
      tasaVentaActual: 0,
      cargandoTasa: false,
      
      // Pago
      showPagoModal: false,
      prestamoSeleccionado: null,
      pagoForm: {
        idPrestamo: null,
        montoUsd: 0,
        montoBs: 0,
        tasaBsUsd: 0,
        metodoPago: 'efectivo',
        referencia: '',
        observaciones: '',
        idUsuarioRegistro: null
      },
      registrandoPago: false,
      
      // Confirmación
      showConfirmModal: false,
      cancelando: false
    };
  },
  computed: {
    filteredPrestamos() {
      let filtered = [...this.prestamos];
      
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(p => 
          p.numeroPrestamo?.toLowerCase().includes(q) ||
          p.nombrePrestatario?.toLowerCase().includes(q) ||
          p.nombreUsuario?.toLowerCase().includes(q) ||
          p.nombreCliente?.toLowerCase().includes(q) ||
          p.cedulaPrestatario?.toLowerCase().includes(q)
        );
      }
      
      return filtered;
    },
    paginatedPrestamos() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredPrestamos.slice(start, start + this.itemsPerPage);
    },
    totalPages() {
      return Math.ceil(this.filteredPrestamos.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
    startItem() {
      return (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.itemsPerPage, this.filteredPrestamos.length);
    }
  },
  mounted() {
    this.loadPrestamos();
    const user = getCurrentUser();
    this.pagoForm.idUsuarioRegistro = user?.idUsuario || 1;
    this.cargarTasaVenta();
  },
  methods: {
    async loadPrestamos() {
      this.loading = true;
      try {
        let response;
        if (this.filterEstado === 'activo') {
          response = await obtenerPrestamosActivos();
        } else if (this.filterEstado === 'vencido') {
          response = await obtenerPrestamosVencidos();
        } else if (this.filterEstado !== 'all') {
          response = await obtenerPrestamosPorEstado(this.filterEstado);
        } else if (this.filterTipo !== 'all') {
          response = await obtenerPrestamosPorTipo(this.filterTipo);
        } else {
          response = await obtenerPrestamos();
        }
        
        if (response.success) {
          this.prestamos = response.data || [];
        }
        
        // Cargar estadísticas
        const statsRes = await obtenerEstadisticasPrestamos();
        if (statsRes.success) {
          this.estadisticas = statsRes.data || {};
        }
      } catch (error) {
        console.error('Error cargando préstamos:', error);
      } finally {
        this.loading = false;
      }
    },

    // ========== TASA DE VENTA ==========
    async cargarTasaVenta() {
      this.cargandoTasa = true;
      try {
        const response = await obtenerTasaVentaActual();
        if (response.success && response.data) {
          this.tasaVentaActual = response.data.precio_usd_bs || response.data.precioUsdBs || 0;
          this.pagoForm.tasaBsUsd = this.tasaVentaActual;
          console.log('💰 Tasa de venta actual:', this.tasaVentaActual);
          if (this.pagoForm.montoUsd > 0) {
            this.calcularMontoBs();
          }
        } else {
          console.warn('No se pudo obtener la tasa de venta:', response.error);
          this.tasaVentaActual = 0;
          this.pagoForm.tasaBsUsd = 0;
        }
      } catch (error) {
        console.error('Error cargando tasa de venta:', error);
        this.tasaVentaActual = 0;
        this.pagoForm.tasaBsUsd = 0;
      } finally {
        this.cargandoTasa = false;
      }
    },

    // ========== CALCULAR MONTO EN BS ==========
    calcularMontoBs() {
      if (this.pagoForm.tasaBsUsd > 0 && this.pagoForm.montoUsd > 0) {
        this.pagoForm.montoBs = parseFloat((this.pagoForm.montoUsd * this.pagoForm.tasaBsUsd).toFixed(2));
      } else {
        this.pagoForm.montoBs = 0;
      }
    },

    abrirModalPago(prestamo) {
      this.prestamoSeleccionado = prestamo;
      this.pagoForm.idPrestamo = prestamo.idPrestamo;
      this.pagoForm.montoUsd = prestamo.saldoPendienteUsd || 0;
      this.pagoForm.montoBs = 0;
      this.pagoForm.tasaBsUsd = this.tasaVentaActual || 0;
      this.pagoForm.metodoPago = 'efectivo';
      this.pagoForm.referencia = '';
      this.pagoForm.observaciones = '';
      this.showPagoModal = true;
      if (this.pagoForm.tasaBsUsd > 0 && this.pagoForm.montoUsd > 0) {
        this.calcularMontoBs();
      }
    },

    async registrarPago() {
      if (!this.pagoForm.montoUsd && !this.pagoForm.montoBs) {
        alert('Debe ingresar al menos un monto');
        return;
      }
      if (!this.pagoForm.tasaBsUsd) {
        alert('La tasa de cambio es requerida');
        return;
      }

      this.registrandoPago = true;
      try {
        const result = await registrarPagoRapido(this.pagoForm);
        if (result.success) {
          this.showPagoModal = false;
          await this.loadPrestamos();
          alert('Pago registrado exitosamente');
        } else {
          alert(result.error || 'Error al registrar el pago');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error al registrar el pago');
      } finally {
        this.registrandoPago = false;
      }
    },

    abrirModalCancelar(prestamo) {
      this.prestamoSeleccionado = prestamo;
      this.showConfirmModal = true;
    },

    async confirmarCancelacion() {
      if (!this.prestamoSeleccionado) return;
      
      this.cancelando = true;
      try {
        const result = await cancelarPrestamo(this.prestamoSeleccionado.idPrestamo);
        if (result.success) {
          this.showConfirmModal = false;
          await this.loadPrestamos();
          alert('Préstamo cancelado exitosamente');
        } else {
          alert(result.error || 'Error al cancelar el préstamo');
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Error al cancelar el préstamo');
      } finally {
        this.cancelando = false;
      }
    },

    // ========== UTILIDADES ==========
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
    },
    formatCurrencyBs(value) {
      return new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'VES' }).format(value || 0);
    },
    formatDate(fecha) {
      if (!fecha) return 'N/A';
      try {
        return new Date(fecha).toLocaleDateString('es-VE', { year: 'numeric', month: '2-digit', day: '2-digit' });
      } catch {
        return fecha;
      }
    },
    formatearEstado(estado) {
      const map = {
        'activo': 'Activo',
        'pagado': 'Pagado',
        'vencido': 'Vencido',
        'parcial': 'Parcial',
        'cancelado': 'Cancelado'
      };
      return map[estado?.toLowerCase()] || estado || 'Desconocido';
    },
    formatearTipo(tipo) {
      const map = {
        'empleado': 'Empleado',
        'cliente': 'Cliente',
        'externo': 'Externo'
      };
      return map[tipo?.toLowerCase()] || tipo || 'Desconocido';
    },
    getEstadoClass(estado) {
      const map = {
        'activo': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
        'pagado': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300',
        'vencido': 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-300',
        'parcial': 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
        'cancelado': 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700/30 dark:text-gray-400'
      };
      return map[estado?.toLowerCase()] || map.activo;
    },
    getEstadoIcon(estado) {
      const map = {
        'activo': 'mdi:check-circle',
        'pagado': 'mdi:check-circle',
        'vencido': 'mdi:alert-circle',
        'parcial': 'mdi:clock',
        'cancelado': 'mdi:close-circle'
      };
      return map[estado?.toLowerCase()] || 'mdi:circle';
    },
    getTipoClass(tipo) {
      const map = {
        'empleado': 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'cliente': 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'externo': 'bg-gray-50 text-gray-700 dark:bg-gray-700/30 dark:text-gray-300'
      };
      return map[tipo?.toLowerCase()] || map.externo;
    },
    getTipoIcon(tipo) {
      const map = {
        'empleado': 'mdi:account-tie',
        'cliente': 'mdi:account',
        'externo': 'mdi:account-outline'
      };
      return map[tipo?.toLowerCase()] || 'mdi:account';
    },
    getVencimientoClass(fecha) {
      if (!fecha) return 'text-gray-400';
      const dias = this.calcularDias(fecha);
      if (dias < 0) return 'text-rose-600 font-black';
      if (dias <= 3) return 'text-amber-600 font-black';
      if (dias <= 7) return 'text-blue-600';
      return 'text-gray-500';
    },
    getDiasTexto(fecha) {
      if (!fecha) return 'Sin fecha';
      const dias = this.calcularDias(fecha);
      if (dias === null) return 'Sin fecha';
      if (dias < 0) return `${Math.abs(dias)} días vencido`;
      if (dias === 0) return 'Vence hoy';
      if (dias === 1) return 'Vence mañana';
      return `${dias} días`;
    },
    calcularDias(fecha) {
      if (!fecha) return null;
      const hoy = new Date();
      const vencimiento = new Date(fecha);
      return Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24));
    },
    handleSearch: debounce(function() {
      this.currentPage = 1;
    }, 300)
  }
};
</script>

<style scoped>
.text-lexend { font-family: 'Lexend', sans-serif; }
.custom-scrollbar::-webkit-scrollbar { height: 6px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; }

@keyframes zoomIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-zoomIn { animation: zoomIn 0.2s ease-out; }

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fadeInUp { animation: fadeInUp 0.4s ease-out forwards; }
.delay-100 { animation-delay: 0.1s; }
.delay-200 { animation-delay: 0.2s; }

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
.animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }

/* Estilos para scroll en móviles */
@media (max-width: 640px) {
  .max-h-\[95vh\] {
    max-height: 95vh;
  }
  .overflow-y-auto {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
}
</style>